





































































































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import {DescTypeOption, SelectOption} from "@/util/selectOption";
import axios from 'axios';
import spacetime, {Spacetime} from 'spacetime';
import {currency} from "@/util/formatter";
import {TransactionData, RecurringTransactionData} from "@/util/transactionData";
import "@/util/dateExtension";

interface DateRange {
    start: Date;
    end: Date;
}

@Component
export default class BalanceTable extends Vue {

    @Prop()
    incomeDescriptionOptions!: SelectOption[];

    @Prop()
    expenseDescriptionOptions!: SelectOption[];

    @Prop()
    descTypesUsedInCurrentTaxPeriod!: DescTypeOption[];

    private incomeTransactions: TransactionData[] = [];
    private expenseTransactions: TransactionData[] = [];
    private totalIncome: string = 'Loading…';
    private totalExpenses: string = 'Loading…';

    private incomeRecurring: RecurringTransactionData[] = [];
    private expenseRecurring: RecurringTransactionData[] = [];
    private isLoadingRecurringEntries: boolean = false;

    private rangeOptions: SelectOption[] = [
        {
            name: 'Last 7 Days',
            id: 0
        },
        {
            name: 'Last 30 Days',
            id: 1
        },
        {
            name: 'Last Tax Year',
            id: 2
        },
        {
            name: 'Current Tax Year',
            id: 3
        },
        {
            name: 'Custom Date Range',
            id: 4
        }
    ];
    private rangeOption: SelectOption | null = null;
    private customDateRange: DateRange | null = null;
    private oldNonCustomDateRangeOption: SelectOption | null = this.rangeOption;

    private isLoadingBalances: boolean = false;
    private isDeleting: boolean = false;
    private activeTab: string = window.location.hash.replace('#', '');
    private listType: 'balances' | 'recurring' = 'balances'; // either 'balances' or 'recurring'

    private selectedTransactionIds: number[] = [];

    private get isDatePickerActive(): boolean {
        return this.rangeOption?.id === 4 && this.customDateRange === null;
    }

    private get isInSelectionMode(): boolean {
        return this.selectedTransactionIds.length > 0;
    }

    private get descriptionOptions(): SelectOption[] {
        return this.activeTab === 'income' ? this.incomeDescriptionOptions : this.expenseDescriptionOptions;
    }

    private get editTitle(): string {
        return this.activeTab === 'income' ? 'Edit Income' : 'Edit Expense';
    }

    private get editRecurringTitle(): string {
        return this.activeTab === 'income' ? 'Edit Recurring Income' : 'Edit Recurring Expense';
    }

    private get activeTransactions(): TransactionData[] {
        if (this.activeTab === 'income') {
            return this.incomeTransactions
        } else {
            return this.expenseTransactions;
        }
    }

    private get activeRecurringEntries(): RecurringTransactionData[] {
        if (this.activeTab === 'income') {
            return this.incomeRecurring;
        } else {
            return this.expenseRecurring;
        }
    }

    private get totalAmount(): string {
        return this.activeTab === 'income' ? this.totalIncome : this.totalExpenses;
    }

    private get totalClass(): string {
        return this.activeTab === 'income' ? 'text-green' : 'text-red';
    }

    private mounted() {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const searchRangeFrom = urlSearchParams.get('from');
        const searchRangeTo = urlSearchParams.get('to');
        if (searchRangeFrom && searchRangeTo) {
            this.customDateRange = {
                start: spacetime(searchRangeFrom, 'utc').toLocalDate(),
                end: spacetime(searchRangeTo, 'utc').toLocalDate(),
            }
        } else {
            const searchRange = urlSearchParams.get('range') || '';
            switch (searchRange) {
                case 'week':
                    this.rangeOption = this.rangeOptions[0];
                    break;
                case 'month':
                case '':
                    this.rangeOption = this.rangeOptions[1];
                    break;
                case 'last_tax_year':
                    this.rangeOption = this.rangeOptions[2];
                    break;
                case 'tax_year':
                    this.rangeOption = this.rangeOptions[3];
                    break;
            }
        }

        if (!this.activeTab) {
            this.activeTab = 'income';
        }
        this.fetchRecurringEntries();
    }

    @Watch('rangeOption')
    private onRangeOptionChanged(option: SelectOption, oldOption: SelectOption) {
        // we've just selected our placeholder date range option - do nothing
        if (option.id === -1) {
            return;
        }

        if (this.customDateRange && this.rangeOptions.length === 6) {
            this.rangeOptions.pop();
        }

        if (option.id === 4) {
            this.customDateRange = null;
        } else if (option.id !== this.oldNonCustomDateRangeOption?.id) {
            this.oldNonCustomDateRangeOption = option;
            this.fetchTransactions();
        }
    }

    @Watch('customDateRange')
    private onCustomDateRangePicked(newRange: DateRange | null) {
        if (newRange) {
            const placeholderDateOption = {
                id: -1,
                name: `${newRange.start.toLocaleDateString('en-GB')} - ${newRange.end.toLocaleDateString('en-GB')}`
            };
            this.rangeOptions.push(placeholderDateOption);
            this.oldNonCustomDateRangeOption = null;
            this.rangeOption = placeholderDateOption;
            this.fetchTransactions();
        }
    }

    @Watch('activeTab')
    private onActiveTabChanged(newTab: string) {
        this.selectedTransactionIds = [];
        window.location.hash = `#${newTab}`;
    }

    @Watch('listType')
    private onListTypeChanged(newType: string) {
        this.selectedTransactionIds = [];
    }

    private onTransactionCheckedStateChanged(balanceId: number, isChecked: boolean) {
        if (isChecked && !this.selectedTransactionIds.includes(balanceId)) {
            this.selectedTransactionIds.push(balanceId);
        } else {
            this.selectedTransactionIds = this.selectedTransactionIds.filter(id => id !== balanceId);
        }
    }

    private dismissDatePicker() {
        this.customDateRange = null;
        this.rangeOption = this.oldNonCustomDateRangeOption ?? this.rangeOptions[1];
    }

    private updateHistoryStateWithRange(range: string) {
        const search = `?range=${range}`;
        if (search === window.location.search) {
            return;
        }
        const newUrl = `/balance${search}${window.location.hash}`;

        window.history.replaceState({}, '', newUrl)
    }

    private fetchTransactions() {
        this.isLoadingBalances = true;
        this.selectedTransactionIds = [];
        this.incomeTransactions = [];
        this.expenseTransactions = [];
        this.totalIncome = this.totalExpenses = 'Loading…';

        let to = spacetime.now('UTC');
        let from: Spacetime | null = null;
        let custom: string | null = null;
        switch (this.rangeOption!.id) {
            // Custom date range
            case -1:
                from = spacetime(this.customDateRange!.start.toUtcDate());
                to = spacetime(this.customDateRange!.end.toUtcDate());
                const url = `/balance?from=${from.format('yyyy-mm-dd')}&to=${to.format('yyyy-mm-dd')}${window.location.hash}`;
                window.history.replaceState({}, '', url)
                break;
            // Week
            case 0:
                from = to.subtract(1, 'week');
                this.updateHistoryStateWithRange('week');
                break;
            // Month
            case 1:
                from = to.subtract(30, 'day');
                this.updateHistoryStateWithRange('month');
                break;
            // Last Tax Year
            case 2:
                custom = 'last_tax_year';
                this.updateHistoryStateWithRange('last_tax_year');
                break;
            // Current Tax Year
            case 3:
                custom = 'tax_year';
                this.updateHistoryStateWithRange('tax_year');
                break;
            default:
                this.$honeybadger.notify('Unsupported date range option selected: ' + JSON.stringify(this.rangeOption));
                break;
        }

        axios.get('/balances', {
            params: {
                from: from?.format('yyyy-mm-dd') || null,
                to: to.format('yyyy-mm-dd'),
                custom
            }
        }).then(result => {
            const data = result.data;
            this.incomeTransactions = data.incomes;
            this.expenseTransactions = data.expenses;
            this.totalIncome = currency(data.totalIncome, true);
            this.totalExpenses = currency(data.totalExpenses, true);
        }).catch(error => {
            //TODO: Display error on screen
            console.log(error);
        }).finally(() => {
            this.isLoadingBalances = false;
        });
    }

    private fetchRecurringEntries() {
        if (this.isLoadingRecurringEntries) {
            return;
        }

        this.isLoadingRecurringEntries = true;
        this.expenseRecurring = [];
        this.incomeRecurring = [];

        axios.get('/recurringBalances').then(result => {
            const data = result.data;
            this.expenseRecurring = data.expenseRecurring;
            this.incomeRecurring = data.incomeRecurring;
        }).catch(error => {
            //TODO: Display error on screen
            console.log(error);
        }).finally(() => {
            this.isLoadingRecurringEntries = false;
        });
    }

    private deleteSelectedTransactions() {
        if (this.isDeleting || this.selectedTransactionIds.length === 0) {
            return;
        }

        this.isDeleting = true;

        axios.post('/balance/multipleDestroy', {
            'balanceIds': this.selectedTransactionIds
        }).then(result => {
            this.selectedTransactionIds = [];
            window.location.reload();
        }).catch(error => {
            console.error(error);
            // TODO: handle error, though as of 25.03 the server call cannot error.
        }).finally(() => {
            this.isDeleting = false;
        })
    }
}
